import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { camelizeKeys } from 'humps';

import api, { getData } from 'services/api';

import {
	FIXED_HEADERS,
	applyColumnMapping,
	buildDefaultMapping,
	fileTypes,
	parseCsvData,
	parseExcelFile,
	processDataWithPlaceholderEmails,
	processSingleColumnData,
} from './utils';
import { ColumnMapping, CompedTicketData, EditableRow, ExpectedField, FileData, TicketTypeSummary } from './types';
import { CompIssued } from 'features/PunchupPages/SendTickets/types';

export interface UseGuestListUploadOptions {
	/** Receives one {email, transactionId} per recipient from the versioned CSV route, so a caller
	 *  can mark what this send just created. Empty when the deployed API predates that route. */
	onSuccess: (issued: CompIssued[]) => void;
	onError?: (error: unknown) => void;
	/** Called after a file is parsed if the rows contain fixable issues (unmatched ticket types,
	 *  exceeded inventory). Each string is a human-readable issue summary. */
	onLoadWarnings?: (issues: string[]) => void;
}

export interface UseGuestListUploadReturn {
	fileData: FileData | null;
	ticketTypes: TicketTypeSummary[];
	editableRows: EditableRow[];
	canSend: boolean;
	isSending: boolean;
	totalQty: number;
	/** Raw header names from the uploaded multi-column file (empty for single-column or no file). */
	originalHeaders: string[];
	/** Current expected-field → column-index mapping (null until a multi-column file is parsed). */
	columnMapping: ColumnMapping | null;
	/** Override which uploaded column feeds an expected field; re-derives the editable rows live. */
	setColumnMappingField: (field: ExpectedField, index: number | null) => void;
	getExceededTicketTypes: () => TicketTypeSummary[];
	handleFile: (file: File) => Promise<void>;
	handleDeleteFile: () => void;
	/** Clear the whole upload back to its empty initial state — file, rows, raw parse, and column
	 *  mapping. Used when a parent restarts the send flow; the file-removal path shares the same clear. */
	reset: () => void;
	updateRow: (id: string, update: Partial<Omit<EditableRow, 'id'>>) => void;
	addRow: () => void;
	deleteRow: (id: string) => void;
	handleSendTickets: () => void;
	buildCsvContent: () => string;
}

let rowCounter = 0;
const generateRowId = () => `row_${++rowCounter}`;

const readFileAsText = (file: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (e) => resolve((e.target?.result as string) ?? '');
		reader.onerror = () => reject(new Error('Failed to read file'));
		reader.readAsText(file);
	});

const toEditableRows = (data: FileData['data'], ticketTypes: TicketTypeSummary[]): EditableRow[] =>
	data.map((row) => {
		const rawName = (row['Ticket Type'] ?? '').trim().toLowerCase();
		const matchedType = rawName ? ticketTypes.find((tt) => tt.name.toLowerCase() === rawName) : undefined;
		return {
			id: generateRowId(),
			email: row['Email'] ?? '',
			firstName: row['First Name'] ?? '',
			lastName: row['Last Name'] ?? '',
			quantity: Math.max(0, parseInt(row['Quantity'] ?? '1', 10) || 0),
			ticketTypeId: matchedType?.iD ?? null,
		};
	});

export const useGuestListUpload = (
	eventId: string,
	{ onSuccess, onError, onLoadWarnings }: UseGuestListUploadOptions
): UseGuestListUploadReturn => {
	const [fileData, setFileData] = useState<FileData | null>(null);
	const [editableRows, setEditableRows] = useState<EditableRow[]>([]);
	// Raw parse output retained so a column-mapping change can re-derive rows
	// without re-uploading. Only populated for multi-column files.
	const [originalHeaders, setOriginalHeaders] = useState<string[]>([]);
	const [rawData, setRawData] = useState<CompedTicketData[]>([]);
	const [columnMapping, setColumnMapping] = useState<ColumnMapping | null>(null);

	const { data: ticketTypes = [] } = useQuery({
		queryKey: ['ticketTypes', eventId],
		queryFn: () => getData(`/public/ticketing/ticket_types?event_id=${eventId}`),
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		select: (data: any[]) => camelizeKeys(data) as unknown as TicketTypeSummary[],
		enabled: !!eventId,
	});

	const getExceededTicketTypes = (): TicketTypeSummary[] => {
		const qtyByTypeId = new Map<number, number>();
		editableRows.forEach((row) => {
			if (row.ticketTypeId !== null) {
				qtyByTypeId.set(row.ticketTypeId, (qtyByTypeId.get(row.ticketTypeId) ?? 0) + row.quantity);
			}
		});
		return ticketTypes.filter((tt) => {
			const qty = qtyByTypeId.get(tt.iD) ?? 0;
			return qty > tt.amount - tt.sold;
		});
	};

	const updateRow = (id: string, update: Partial<Omit<EditableRow, 'id'>>) =>
		setEditableRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...update } : row)));

	const addRow = () =>
		setEditableRows((prev) => [
			...prev,
			{ id: generateRowId(), email: '', firstName: '', lastName: '', quantity: 1, ticketTypeId: null },
		]);

	const deleteRow = (id: string) => setEditableRows((prev) => prev.filter((row) => row.id !== id));

	const setColumnMappingField = (field: ExpectedField, index: number | null) => {
		if (originalHeaders.length === 0) return;
		const next: ColumnMapping = {
			...(columnMapping ?? buildDefaultMapping(rawData, originalHeaders)),
			[field]: index,
		};
		setColumnMapping(next);
		// applyColumnMapping is the single source of email resolution (it now respects
		// an unmapped Email field). Do NOT post-process with placeholder emails here —
		// that would re-fill a deliberately-emptied Email column with `hello+…` values.
		setEditableRows(toEditableRows(applyColumnMapping(rawData, originalHeaders, next), ticketTypes));
	};

	const buildCsvContent = (): string => {
		if (editableRows.length === 0) return '';
		return editableRows
			.map((row) =>
				[
					row.email,
					row.firstName,
					row.lastName,
					String(row.quantity),
					row.ticketTypeId !== null ? String(row.ticketTypeId) : '',
				].join(',')
			)
			.join('\n');
	};

	const { mutate: handleSendTickets, isLoading: isSending } = useMutation({
		mutationFn: async () => {
			if (editableRows.length === 0) throw new Error('No rows to send');
			const csvContent = buildCsvContent();
			const blob = new Blob([csvContent], { type: 'text/csv' });
			const csvFile = new File([blob], 'comped_tickets.csv', { type: 'text/csv' });
			const formData = new FormData();
			formData.append('file', csvFile);
			// The versioned route reports the transactions it created; the shipped one answers an echo
			// that names nothing printable. A 404 means the API predates the twin — fall back and
			// report none, so a send still succeeds against an older deployment.
			try {
				const { data } = await api.post('/tickets/comp-tickets/csv/v1', formData);
				return camelizeKeys((data as { comps?: unknown[] })?.comps ?? []) as unknown as CompIssued[];
			} catch (error) {
				if ((error as { response?: { status?: number } })?.response?.status !== 404) throw error;
				await api.post('/tickets/comp-tickets/csv', formData);
				return [];
			}
		},
		onSuccess,
		onError,
	});

	const handleFile = async (file: File) => {
		setFileData({ file, data: [], headers: [], columnMapping: [0, 1, 2, 3, 4], isProcessing: true, errors: null });
		setEditableRows([]);
		setOriginalHeaders([]);
		setRawData([]);
		setColumnMapping(null);

		try {
			let csv = '';
			if (file.type === fileTypes.excel || /\.(xlsx|xls)$/i.test(file.name)) {
				csv = await parseExcelFile(file);
			} else if (file.type === fileTypes.csv || /\.csv$/i.test(file.name)) {
				csv = await readFileAsText(file);
			} else {
				setFileData((prev) => (prev ? { ...prev, errors: 'Invalid file type', isProcessing: false } : null));
				return;
			}

			const { data, headers } = await parseCsvData(csv);
			let processedData = data;
			if (headers.length === 1) {
				processedData = processSingleColumnData(data, headers);
			} else {
				// Retain the raw parse so the column-matching UI can re-derive rows
				// when the manager overrides the auto-detected mapping.
				const mapping = buildDefaultMapping(data, headers);
				processedData = applyColumnMapping(data, headers, mapping);
				setOriginalHeaders(headers);
				setRawData(data);
				setColumnMapping(mapping);
			}
			processedData = processDataWithPlaceholderEmails(processedData);

			setFileData({
				file,
				data: processedData,
				headers: [...FIXED_HEADERS],
				columnMapping: [0, 1, 2, 3, 4],
				isProcessing: false,
				errors: null,
			});
			const rows = toEditableRows(processedData, ticketTypes);
			setEditableRows(rows);

			if (onLoadWarnings) {
				const issues: string[] = [];
				const untypedCount = rows.filter((r) => r.ticketTypeId === null).length;
				if (untypedCount > 0) {
					issues.push(
						`${untypedCount} ${
							untypedCount === 1 ? 'row' : 'rows'
						} could not be matched to a ticket type — select one in the table`
					);
				}
				const qtyByTypeId = new Map<number, number>();
				rows.forEach((r) => {
					if (r.ticketTypeId !== null) {
						qtyByTypeId.set(r.ticketTypeId, (qtyByTypeId.get(r.ticketTypeId) ?? 0) + r.quantity);
					}
				});
				const exceededNames = ticketTypes
					.filter((tt) => (qtyByTypeId.get(tt.iD) ?? 0) > tt.amount - tt.sold)
					.map((tt) => tt.name);
				if (exceededNames.length > 0) {
					issues.push(`quantity exceeds available tickets for: ${exceededNames.join(', ')}`);
				}
				if (issues.length > 0) {
					onLoadWarnings(issues);
				}
			}
		} catch {
			setFileData((prev) => (prev ? { ...prev, errors: 'Error processing file', isProcessing: false } : null));
		}
	};

	// Clear every derived piece of upload state back to the empty initial. Shared by the
	// file-removal action and a full flow reset — both must wipe ALL derived state, not just
	// the file, or stale rows / mapping would survive into the next batch.
	const reset = () => {
		setFileData(null);
		setEditableRows([]);
		setOriginalHeaders([]);
		setRawData([]);
		setColumnMapping(null);
	};

	// Removing the uploaded file returns the upload to its empty state — the same clear as a reset.
	const handleDeleteFile = reset;

	const totalQty = editableRows.reduce((sum, row) => sum + row.quantity, 0);
	const canSend =
		editableRows.length > 0 &&
		!isSending &&
		editableRows.every((row) => row.ticketTypeId !== null && row.quantity > 0 && row.email.trim() !== '') &&
		getExceededTicketTypes().length === 0;

	return {
		fileData,
		ticketTypes,
		editableRows,
		canSend,
		isSending,
		totalQty,
		originalHeaders,
		columnMapping,
		setColumnMappingField,
		getExceededTicketTypes,
		handleFile,
		handleDeleteFile,
		reset,
		updateRow,
		addRow,
		deleteRow,
		handleSendTickets,
		buildCsvContent,
	};
};
